import { INTEGRATOR_APP_FEATURE_CATEGORY } from "@modules/app/service/app.constant";
import { MediaObjectMimeType } from "@plugins/authoring-copilot/types.ts";

export const MAX_KB_TITLE_LENGTH = 50;

export const LA_INTERACTION_LIMIT = {
  LA_POST: { DEFAULT: 20, MIN: 1, MAX: 50 },
  LA_TEAM: { DEFAULT: 5, MIN: 1, MAX: 50 },
};

const ACP_INTERACTIVE_ALLOWED_TYPES = [
  MediaObjectMimeType.PDF,
  MediaObjectMimeType.JPEG,
  MediaObjectMimeType.GIF,
  MediaObjectMimeType.PNG,
  MediaObjectMimeType.JPG,
];
const LEA_SPEECH_ALLOWED_TYPES = [
  MediaObjectMimeType.JPEG,
  MediaObjectMimeType.GIF,
  MediaObjectMimeType.PNG,
  MediaObjectMimeType.JPG,
];
const STANDARD_ALLOWED_TYPES = [
  MediaObjectMimeType.PDF,
  MediaObjectMimeType.JPEG,
  MediaObjectMimeType.GIF,
  MediaObjectMimeType.PNG,
  MediaObjectMimeType.JPG,
  MediaObjectMimeType.DOCX,
  MediaObjectMimeType.PPTX,
  MediaObjectMimeType.XLSX,
  MediaObjectMimeType.TXT,
];

export interface KbValidationConfig {
  /** Maximum number of MEDIA/GOOGLE knowledge bases allowed (TEXT KBs are unlimited) */
  maxMediaFiles: number;
  /** Allowed MIME types; GOOGLE contentType is always allowed when GOOGLE_MIME_TYPES are included */
  allowedMediaTypes: readonly string[];
  /** Per-file size limit in bytes */
  maxPerFileBytes: number;
  /** Total file size limit in bytes */
  maxTotalBytes: number;
  /** Whether to enforce max file count error (false when maxMediaFiles == overall KB limit) */
  checkMaxFiles: boolean;
  /** Allow google file (embedded)*/
  allowGoogleFile: boolean;
}

export type KbValidationErrorType = "MAX_FILES" | "FILE_TYPE" | "FILE_SIZE" | null;

export interface KbFileError {
  knowledgeBaseId: number;
  errorType: "FILE_TYPE" | "PER_FILE_SIZE";
  message: string;
}

export interface KbValidationResult {
  valid: boolean;
  errorType: KbValidationErrorType;
  fileErrors: KbFileError[];
  bannerMessage?: string;
  toastTitle?: string;
  toastMessage?: string;
}

const MB = 1024 * 1024;

export const KB_VALIDATION_CONFIG: Record<string, KbValidationConfig> = {
  // ACP Authoring: 20 max KBs = 20 max media → count check not needed
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_MODULE]: {
    maxMediaFiles: 20,
    allowedMediaTypes: STANDARD_ALLOWED_TYPES,
    maxPerFileBytes: 20 * MB,
    maxTotalBytes: 20 * MB,
    checkMaxFiles: false,
    allowGoogleFile: true,
  },
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_ACTIVITY]: {
    maxMediaFiles: 20,
    allowedMediaTypes: STANDARD_ALLOWED_TYPES,
    maxPerFileBytes: 20 * MB,
    maxTotalBytes: 20 * MB,
    checkMaxFiles: false,
    allowGoogleFile: true,
  },
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT]: {
    maxMediaFiles: 20,
    allowedMediaTypes: STANDARD_ALLOWED_TYPES,
    maxPerFileBytes: 20 * MB,
    maxTotalBytes: 20 * MB,
    checkMaxFiles: false,
    allowGoogleFile: true,
  },
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT_RE]: {
    maxMediaFiles: 20,
    allowedMediaTypes: STANDARD_ALLOWED_TYPES,
    maxPerFileBytes: 20 * MB,
    maxTotalBytes: 20 * MB,
    checkMaxFiles: false,
    allowGoogleFile: true,
  },
  // ACP Interactive: strict limits
  [INTEGRATOR_APP_FEATURE_CATEGORY.ACP_HTML5]: {
    maxMediaFiles: 5,
    allowedMediaTypes: ACP_INTERACTIVE_ALLOWED_TYPES,
    maxPerFileBytes: 5 * MB,
    maxTotalBytes: 5 * MB,
    checkMaxFiles: true,
    allowGoogleFile: true,
  },
  // LEA
  [INTEGRATOR_APP_FEATURE_CATEGORY.LA_SPEECH]: {
    maxMediaFiles: 5,
    allowedMediaTypes: LEA_SPEECH_ALLOWED_TYPES,
    maxPerFileBytes: 5 * MB,
    maxTotalBytes: 5 * MB,
    checkMaxFiles: true,
    allowGoogleFile: false,
  },
  [INTEGRATOR_APP_FEATURE_CATEGORY.LA_TEXT_QUERY]: {
    maxMediaFiles: 5,
    allowedMediaTypes: STANDARD_ALLOWED_TYPES,
    maxPerFileBytes: 5 * MB,
    maxTotalBytes: 5 * MB,
    checkMaxFiles: true,
    allowGoogleFile: true,
  },
} as const;

export const LEARNING_ASSISTANT_TYPE = {
  TEXT: "TEXT",
  SPEECH: "SPEECH",
  SPEECH_AND_TEXT: "SPEECH_AND_TEXT",
} as const;

export const LEA_SPEECH_DURATION_LIMIT = {
  DEFAULT: 10,
  MIN: 5,
  MAX: 15,
};

export const LEA_SPEECH_RELATED_TYPES: string[] = [
  LEARNING_ASSISTANT_TYPE.SPEECH,
  LEARNING_ASSISTANT_TYPE.SPEECH_AND_TEXT,
];

export const LEA_SPEED_DEFAULT = 1;

export const LEA_SPEED_OPTIONS = [
  { value: 0.75, labelKey: "learning_assistant_settings_subpage_speed_slow_option" },
  { value: 1, labelKey: "learning_assistant_settings_subpage_speed_normal_option" },
  { value: 1.25, labelKey: "learning_assistant_settings_subpage_speed_fast_option" },
] as const;

export const LEA_VOICE_AGENTS = ["verse", "coral", "marin", "alloy", "cedar"] as const;

export const LEA_VOICE_AGENT_DEFAULT: string = LEA_VOICE_AGENTS[0];

export const LEA_SHOW_TRANSCRIPT_DEFAULT = true;
